import React, { useMemo, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useWorkflow } from "./WorkflowContext";

export type WorkflowStep = {
  id: number;
  name: string;
  path: string;
  status: "completed" | "active" | "PENDING" | "ACCEPTED";
};

interface WorkflowProgressProps {
  steps: WorkflowStep[];
  currentStepId: number;
  className?: string;
  status?: any;
}

const WorkflowProgress = ({
  steps,
  currentStepId,
  className,
  status = 'Created',
}: WorkflowProgressProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(currentStepId.toString());
  const { application, canNavigateToStep } = useWorkflow();

  // Update active tab when currentStepId changes or when location changes
  useEffect(() => {
    setActiveTab(currentStepId.toString());
  }, [currentStepId, location.pathname]);
  console.log('debug workflow step data', status);
  // Determine which step is currently active based on the current route
  const getCurrentActiveStep = () => {
    const currentPath = location.pathname;

    // Try multiple ways to match the current route
    const currentStep = steps.find((step) => {
      // Direct path match
      if (currentPath.includes(step.path)) {
        return true;
      }

      // Match by step ID in URL
      if (currentPath.includes(`step-${step.id}`)) {
        return true;
      }

      // Match by workflow step name
      const stepNameInUrl = step.name.toLowerCase().replace(/\s+/g, "-");
      if (currentPath.includes(`/cx/consumer-onboarding/workflow/${stepNameInUrl}`)) {
        return true;
      }

      // Match specific workflow routes
      if (
        currentPath.includes("/cx/consumer-onboarding/workflow/approval") &&
        step.name.toLowerCase().includes("approval")
      ) {
        return true;
      }
      if (
        currentPath.includes("/cx/consumer-onboarding/workflow/inspection") &&
        step.name.toLowerCase().includes("inspection")
      ) {
        return true;
      }
      if (
        currentPath.includes("/cx/consumer-onboarding/workflow/installation") &&
        step.name.toLowerCase().includes("installation")
      ) {
        return true;
      }
      if (
        currentPath.includes("/cx/consumer-onboarding/workflow/activation") &&
        step.name.toLowerCase().includes("activation")
      ) {
        return true;
      }
      if (
        currentPath.includes("/cx/consumer-onboarding/view") &&
        step.name.toLowerCase().includes("review")
      ) {
        return true;
      }

      return false;
    });

    const activeStepId = currentStep?.id || currentStepId;
    return activeStepId;
  };

  // Handle tab click with navigation restrictions
  const handleStepClick = (stepId: number) => {
  if (!application) return;

  const currentStep = application.currentStep;
  const canNavigate = canNavigateToStep(stepId, currentStep);

  // Check extra condition for last step - only allow if installation is completed
  if (stepId === steps.length) {
    const applicationStatus = application.statusDisplay?.toLowerCase() || application.apiStatus?.toLowerCase() || '';
    if (applicationStatus !== "installation completed" && applicationStatus !== "approved") {
      return; // Block navigation to last step
    }
  }

  if (canNavigate) {
    const step = steps.find((s) => s.id === stepId);
    if (step) {
      setActiveTab(stepId.toString());
      navigate(step.path);
    }
  }
};

// Determine if a step can be clicked
const isStepClickable = (stepId: number) => {
  if (!application) return false;

  // Add condition for last step - only allow if installation is completed
  if (stepId === steps.length) {
    const applicationStatus = application.statusDisplay?.toLowerCase() || application.apiStatus?.toLowerCase() || '';
    if (applicationStatus !== "installation completed" && applicationStatus !== "approved") {
      return false;
    }
  }

  return canNavigateToStep(stepId, application.currentStep);
};
  const activeStepId = getCurrentActiveStep();
  // Check if a specific step is actually completed (submitted)
  const isStepCompleted = (stepId: number) => {
    if (!application) return false;
    
    // Check completion based on actual submission status or workflow state
    // This is the key fix - check actual completion rather than just step comparison
    switch (stepId) {
      case 1: // Application Review
        return application.currentStep > 1;
      case 2: // Site Inspection  
        return application.currentStep > 2;
      case 3: // Approval
        return application.currentStep > 3;
      case 4: // Installation - THIS IS THE FIX
        return application.currentStep > 4;
      case 5: // Activation
        return application.currentStep > 5;
      default:
        return stepId < application.currentStep;
    }
  };

  // Get step display status
  const getStepDisplayStatus = (step: WorkflowStep, stepId: number) => {
    if (!application) return { isActive: false, isCompleted: false, isPending: true };

    const currentStep = application.currentStep;
    const isActive = stepId === activeStepId;
    const isCompleted = isStepCompleted(stepId); // Use the new completion check
    const isPending = stepId > currentStep;

    return { isActive, isCompleted, isPending };
  };

  return (
    <div className="flex border-b mb-4">
      {steps.map((step, index) => {
        const { isActive, isCompleted, isPending } = getStepDisplayStatus(step, step.id);
        const clickable = isStepClickable(step.id);

        return (
          <button
            key={step.id}
            onClick={() => handleStepClick(step.id)}
            disabled={!clickable}
            className={`
              flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200
              ${
                isActive
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                  : isCompleted
                  ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                  : isPending
                  ? "text-gray-400"
                  : "text-gray-500 hover:text-gray-700"
              }
              ${
                !clickable 
                  ? "opacity-50 cursor-not-allowed" 
                  : "cursor-pointer hover:bg-gray-50"
              }
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
            `}
            title={
                !clickable && isPending
                ? "Complete previous steps to access this step"
                : !clickable
                ? "Complete previous steps to access this step"
                : isCompleted
                ? "Click to revisit this completed step"
                : isActive
                ? "Current step"
                : "Click to navigate to this step"
            }
          >
            <div className="flex items-center justify-center gap-2">
              {isCompleted && <Check className="h-4 w-4" />}
              {isPending && !clickable && (
                <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
              )}
              {isActive && (
                <div className="h-4 w-4 rounded-full bg-blue-600" />
              )}
              <span className="truncate">{step.name}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default WorkflowProgress;